import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  EditOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BarsOutlined,
  LaptopOutlined,
  NumberOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useAppSelector } from "../../../../store/hooks";
import { getRestaurantName } from "../../../../lib/restaurant";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { KbdChip } from "../modalPrimitives";
import { useOrderingLogout } from "../useOrderingLogout";
import type { CatalogView } from "../../../../api/configuration";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../hooks/useConfiguration";
import { useHasPluCodes } from "./catalogSort";

interface OrderFooterProps {
  onBack?: () => void;
  onInfo?: () => void;
  onModify?: () => void;
  hasSelectedItem?: boolean;
  /** „Клиенти" (същият модал като на екрана с масите) — подава се само с
   *  правото select_customer; undefined = бутонът не се рендира. */
  onClients?: () => void;
  /** true само когато няма нито сметка, нито нова маса в процес на откриване
   *  (при нова маса изборът изчаква и пътува с CreateBillIn). */
  clientsDisabled?: boolean;
  /** Изглед на каталога — плочки (грид) / таблица (лист); F7 го обръща. */
  catalogView: CatalogView;
  onCatalogViewChange: (view: CatalogView) => void;
  /** Екранната клавиатура (F8). */
  keyboardOpen: boolean;
  onToggleKeyboard: () => void;
  /** Името на масата — стои вдясно до името на заведението (широк екран). */
  tableName?: string;
  /** Вместо „Маса …" — собствен надпис на мястото (напр. „Създаване на обедно меню"). */
  contextTitle?: string;
}

interface BtnProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
  active?: boolean;
  /** Подсказка при заключено действие (локален режим, docs/edge.md). */
  lockedTooltip?: string;
}

/** Ghost бутон от долната лента (бял, с pos-ctl рамка и kbd chip горе-дясно);
 *  active = маркиран (акцентен фон), за превключвателите. Под 1440px
 *  (isWide) е по-стегнат, за да се събере целият футър на 1280. */
const Btn = ({
  icon,
  label,
  onClick,
  disabled,
  shortcut,
  active,
  lockedTooltip,
}: BtnProps) => {
  const { isWide } = useViewport();
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !!lockedTooltip}
      aria-pressed={active}
      className={`relative inline-flex h-11 items-center justify-center gap-1.5 rounded-[11px] border font-semibold whitespace-nowrap shrink-0 transition-colors cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed ${
        lockedTooltip ? "pointer-events-none" : ""
      } ${
        isWide ? "px-4 text-[14px]" : "px-3 text-[13px]"
      } ${
        active
          ? "border-pos-accent bg-pos-accent text-white"
          : "border-pos-ctl bg-pos-card text-pos-ink2 hover:bg-pos-panel"
      }`}
    >
      <span
        className={`inline-flex items-center justify-center text-[14px] ${
          active ? "text-white/80" : "text-pos-dim"
        }`}
      >
        {icon}
      </span>
      <span>{label}</span>
      {shortcut && (
        <KbdChip
          shortcut={shortcut}
          colorClass={active ? "text-white/60" : "text-pos-faint"}
        />
      )}
    </button>
  );
  // Обвивката носи подсказката на заключено действие: disabled бутон не
  // получава mouse събития, затова той е pointer-events-none.
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span className="inline-flex shrink-0 cursor-not-allowed">{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

/**
 * Долна лента на изглед „Продукти" (само десктоп; горната лента тук няма).
 * Вляво: Назад (Esc) / Инфо / Промени (F9) / Клиенти. В средата: Плочки |
 * Таблица (F7), Клавиатура (F8) и подредбата АБВ ⇄ КОД (личната настройка
 * catalogSort, записва се директно; само ако има PLU кодове).
 * Вдясно: маса · заведение (широк екран),
 * Backoffice, цял екран и профилът (ProfileDropdown — същият като в горната
 * лента на масите). „Поръчай" живее в панела „Преглед на поръчката".
 */
export const OrderFooter = ({
  onBack,
  onInfo,
  onModify,
  hasSelectedItem = false,
  onClients,
  clientsDisabled = false,
  catalogView,
  onCatalogViewChange,
  keyboardOpen,
  onToggleKeyboard,
  tableName,
  contextTitle,
}: OrderFooterProps) => {
  const { t } = useTranslation();
  const { isWide } = useViewport();
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);
  const displayName = names || username || "";
  const restaurantName = getRestaurantName();
  const { handleLogout, logoutModal } = useOrderingLogout();
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const hasPluCodes = useHasPluCodes();
  const sortByPlu = configuration.catalogSort === "plu";

  const segment = (view: CatalogView, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => onCatalogViewChange(view)}
      aria-pressed={catalogView === view}
      className={`relative inline-flex items-center gap-1.5 font-semibold cursor-pointer transition-colors ${
        view === "list" ? "border-l border-pos-ctl" : ""
      } ${isWide ? "pl-3.5 pr-7 text-[14px]" : "pl-3 pr-6 text-[13px]"} ${
        catalogView === view
          ? "bg-pos-marked text-pos-accent-ink"
          : "text-pos-ink2 hover:bg-pos-panel"
      }`}
    >
      {icon}
      {label}
      {/* F7 обръща изгледа — chip-ът стои и на двата бутона */}
      <KbdChip shortcut="F7" colorClass="text-pos-faint" />
    </button>
  );

  return (
    <div
      className={`w-full bg-pos-panel border-t border-pos-line flex items-center py-2.5 ${
        isWide ? "gap-2.5 px-5" : "gap-1.5 px-3.5"
      }`}
    >
      <Btn
        icon={<ArrowLeftOutlined />}
        label={t("common.back")}
        onClick={onBack}
        shortcut="Esc"
      />
      {/* Инфо/Промени са на маркирането — редакторът на обедно меню ги няма */}
      {onInfo && (
        <Btn
          icon={<InfoCircleOutlined />}
          label={t("ordering.info")}
          onClick={onInfo}
          disabled={!hasSelectedItem}
        />
      )}
      {onModify && (
        <Btn
          icon={<EditOutlined />}
          label={t("ordering.modify")}
          onClick={onModify}
          disabled={!hasSelectedItem}
          shortcut="F9"
        />
      )}
      {onClients && (
        <Btn
          icon={<TeamOutlined />}
          label={t("ordering.clients")}
          onClick={onClients}
          disabled={clientsDisabled}
        />
      )}

      <span aria-hidden className="w-px h-7 bg-pos-line shrink-0 mx-0.5" />

      {/* Плочки | Таблица — сегментиран превключвател на изгледа (F7) */}
      <span className="inline-flex h-11 items-stretch rounded-[11px] border border-pos-ctl bg-pos-card overflow-hidden shrink-0">
        {segment(
          "grid",
          <AppstoreOutlined className="text-[15px]" />,
          t("ordering.viewTiles"),
        )}
        {segment(
          "list",
          <BarsOutlined className="text-[15px]" />,
          t("ordering.viewTable"),
        )}
      </span>

      <Btn
        icon={<LaptopOutlined />}
        label={t("ordering.keyboard")}
        onClick={onToggleKeyboard}
        shortcut="F8"
        active={keyboardOpen}
      />

      {/* Подредба на каталога АБВ ⇄ КОД — само когато поне един артикул има
          PLU код (без кодове двете подредби са еднакви) */}
      {hasPluCodes && (
        <Btn
          icon={sortByPlu ? <NumberOutlined /> : <SortAscendingOutlined />}
          label={t(sortByPlu ? "ordering.sortPlu" : "ordering.sortName")}
          onClick={() =>
            updateUserConfiguration({
              catalogSort: sortByPlu ? "alphabetical" : "plu",
            })
          }
        />
      )}

      {/* Дясно: маса · заведение (широк екран) | Backoffice · цял екран · профил */}
      <div className="ml-auto flex items-center gap-3 min-w-0">
        {isWide && (contextTitle || tableName || restaurantName) && (
          <>
            <span className="min-w-0 truncate text-[13px] text-pos-soft">
              {contextTitle ? (
                <span className="font-bold text-pos-ink">{contextTitle}</span>
              ) : (
                tableName && (
                  <span className="font-bold text-pos-ink">
                    {t("closeTable.table")} {tableName}
                  </span>
                )
              )}
              {(contextTitle || tableName) && restaurantName && (
                <span className="text-pos-muted"> · </span>
              )}
              {restaurantName}
            </span>
            <span aria-hidden className="w-px h-8 bg-pos-line shrink-0" />
          </>
        )}
        {/* ProfileDropdown носи Backoffice линка, „цял екран" и името/аватара —
            същият компонент като в горната лента на масите */}
        <ProfileDropdown
          displayName={displayName}
          username={username ?? ""}
          access={access}
          isNarrow={false}
          onLogout={handleLogout}
        />
      </div>
      {logoutModal}
    </div>
  );
};
